/**
 * ClashPresets - Cribbage Clash Track Presets & Hazard Layout Engine
 * Provides curated track presets (The Gauntlet, Speedway, Cutthroat Bottleneck, Classic Clash)
 * and tools to compute track metrics and apply layouts to settings & DOM.
 */

import { appStorage } from '../storage/IndexedDBStorage.js';
import { LayoutSanitizer, MAX_CUSTOM_LAYOUTS } from './LayoutSanitizer.js';

export const CLASH_CUSTOM_LAYOUTS_KEY = 'cc_clash_custom_layouts';

export const CLASH_PRESET_KEYS = Object.freeze({
  STANDARD: 'standard',
  GAUNTLET: 'gauntlet',
  SPEEDWAY: 'speedway',
  BOTTLENECK: 'bottleneck'
});

export const CLASH_PRESETS_481 = Object.freeze({
  standard: {
    key: 'standard',
    name: 'Classic Clash',
    badge: '🏛️ Balanced',
    icon: '🏛️',
    description: 'The standard tournament layout. A balanced mix of forward jumps, tactical lanes, and classic hazard zones.',
    tags: ['Balanced', 'Tournament', 'Standard'],
    hazardCount: 30,
    config: {
      optNoPassingLanes: true,
      optNoPassingLaneCount: 4,
      optNoPassingLane1Start: 40,
      optNoPassingLane2Start: 175,
      optNoPassingLane3Start: 275,
      optNoPassingLane4Start: 415,

      optJump: true,
      optJumpCount: 8,
      optJump1Start: 25, optJump1Dest: 60,
      optJump2Start: 195, optJump2Dest: 160,
      optJump3Start: 295, optJump3Dest: 335,
      optJump4Start: 435, optJump4Dest: 395,
      optJump5Start: 75, optJump5Dest: 110,
      optJump6Start: 145, optJump6Dest: 125,
      optJump7Start: 245, optJump7Dest: 280,
      optJump8Start: 365, optJump8Dest: 400,

      optJail: true,
      optJailCount: 2,
      optJail1Hole: 110,
      optJail2Hole: 315,

      optBar: true,
      optBarCount: 2,
      optBar1Hole: 125,
      optBar2Hole: 375,

      optTimeout: true,
      optTimeoutCount: 2,
      optTimeout1Hole: 75,
      optTimeout2Hole: 255,

      optDetour: true,
      optDetourCount: 2,
      optDetour1Hole: 15,
      optDetour2Hole: 235,

      optRoundAbout: true,
      optRoundAboutCount: 2,
      optRoundAbout1Hole: 145,
      optRoundAbout2Hole: 355,

      optSigns: true,
      optSignsCount: 4,
      optSigns1Hole: 90,
      optSigns2Hole: 215,
      optSigns3Hole: 450,
      optSigns4Hole: 465,

      optStopSign: true,
      optStopSignCount: 8,
      optStopSign1Hole: 35,
      optStopSign2Hole: 70,
      optStopSign3Hole: 105,
      optStopSign4Hole: 155,
      optStopSign5Hole: 205,
      optStopSign6Hole: 265,
      optStopSign7Hole: 345,
      optStopSign8Hole: 425,

      optDice: true,
      optDiceCount: 4,
      optDice1Hole: 50,
      optDice2Hole: 185,
      optDice3Hole: 285,
      optDice4Hole: 410,

      optWheel: true,
      optWheelCount: 4,
      optWheel1Hole: 65,
      optWheel2Hole: 160,
      optWheel3Hole: 260,
      optWheel4Hole: 385,

      optSkunkMarkers: true,
      optSkunkMarkerHole: 360,
      optDoubleSkunkMarkerHole: 240
    }
  },

  gauntlet: {
    key: 'gauntlet',
    name: 'The Gauntlet',
    badge: '⚔️ High Hazard',
    icon: '⚔️',
    description: 'Punishing trap run. Maximum Jail, Bar, and Timeout penalty zones with Stop Signs positioned right before hazards to trap players.',
    tags: ['High Penalty', 'Challenging', 'Survival'],
    hazardCount: 32,
    config: {
      optNoPassingLanes: true,
      optNoPassingLaneCount: 3,
      optNoPassingLane1Start: 70,
      optNoPassingLane2Start: 210,
      optNoPassingLane3Start: 380,
      optNoPassingLane4Start: 440,

      optJump: true,
      optJumpCount: 4,
      optJump1Start: 45, optJump1Dest: 30, // backward penalty
      optJump2Start: 185, optJump2Dest: 165, // backward penalty
      optJump3Start: 330, optJump3Dest: 305, // backward penalty
      optJump4Start: 440, optJump4Dest: 405, // backward penalty
      optJump5Start: 85, optJump5Dest: 120,
      optJump6Start: 150, optJump6Dest: 130,
      optJump7Start: 250, optJump7Dest: 285,
      optJump8Start: 370, optJump8Dest: 405,

      optJail: true,
      optJailCount: 2,
      optJail1Hole: 100,
      optJail2Hole: 310,

      optBar: true,
      optBarCount: 2,
      optBar1Hole: 140,
      optBar2Hole: 360,

      optTimeout: true,
      optTimeoutCount: 2,
      optTimeout1Hole: 60,
      optTimeout2Hole: 230,

      optDetour: true,
      optDetourCount: 2,
      optDetour1Hole: 20,
      optDetour2Hole: 270,

      optRoundAbout: true,
      optRoundAboutCount: 2,
      optRoundAbout1Hole: 170,
      optRoundAbout2Hole: 390,

      optSigns: true,
      optSignsCount: 4,
      optSigns1Hole: 80,
      optSigns2Hole: 200,
      optSigns3Hole: 340,
      optSigns4Hole: 450,

      optStopSign: true,
      optStopSignCount: 8,
      optStopSign1Hole: 15,
      optStopSign2Hole: 55,
      optStopSign3Hole: 95,
      optStopSign4Hole: 135,
      optStopSign5Hole: 225,
      optStopSign6Hole: 265,
      optStopSign7Hole: 305,
      optStopSign8Hole: 355,

      optDice: true,
      optDiceCount: 4,
      optDice1Hole: 65,
      optDice2Hole: 175,
      optDice3Hole: 285,
      optDice4Hole: 400,

      optWheel: true,
      optWheelCount: 4,
      optWheel1Hole: 50,
      optWheel2Hole: 150,
      optWheel3Hole: 275,
      optWheel4Hole: 390,

      optSkunkMarkers: true,
      optSkunkMarkerHole: 360,
      optDoubleSkunkMarkerHole: 240
    }
  },

  speedway: {
    key: 'speedway',
    name: 'Speedway',
    badge: '🚀 Turbo Boost',
    icon: '🚀',
    description: 'Blistering acceleration. All 8 Jump Zones configured as massive forward boosts (+40 to +55 holes) and wide open lanes for lightning-fast matches.',
    tags: ['Ultra Fast', 'High Velocity', 'Forward Boosts'],
    hazardCount: 16,
    config: {
      optNoPassingLanes: false, // Open highway: no passing restrictions
      optNoPassingLaneCount: 0,
      optNoPassingLane1Start: 40,
      optNoPassingLane2Start: 175,
      optNoPassingLane3Start: 275,
      optNoPassingLane4Start: 415,

      optJump: true,
      optJumpCount: 8,
      optJump1Start: 25, optJump1Dest: 75,   // +50
      optJump2Start: 90, optJump2Dest: 145,  // +55
      optJump3Start: 160, optJump3Dest: 215, // +55
      optJump4Start: 235, optJump4Dest: 290, // +55
      optJump5Start: 310, optJump5Dest: 365, // +55
      optJump6Start: 380, optJump6Dest: 430, // +50
      optJump7Start: 440, optJump7Dest: 475, // +35
      optJump8Start: 50, optJump8Dest: 90,   // +40

      optJail: false,
      optJailCount: 0,
      optJail1Hole: 110,
      optJail2Hole: 315,

      optBar: true,
      optBarCount: 1,
      optBar1Hole: 190,
      optBar2Hole: 375,

      optTimeout: false,
      optTimeoutCount: 0,
      optTimeout1Hole: 75,
      optTimeout2Hole: 255,

      optDetour: false,
      optDetourCount: 0,
      optDetour1Hole: 15,
      optDetour2Hole: 235,

      optRoundAbout: false,
      optRoundAboutCount: 0,
      optRoundAbout1Hole: 145,
      optRoundAbout2Hole: 355,

      optSigns: true,
      optSignsCount: 2,
      optSigns1Hole: 120,
      optSigns2Hole: 300,
      optSigns3Hole: 450,
      optSigns4Hole: 465,

      optStopSign: true,
      optStopSignCount: 2,
      optStopSign1Hole: 210,
      optStopSign2Hole: 420,
      optStopSign3Hole: 105,
      optStopSign4Hole: 155,
      optStopSign5Hole: 205,
      optStopSign6Hole: 265,
      optStopSign7Hole: 345,
      optStopSign8Hole: 425,

      optDice: true,
      optDiceCount: 2,
      optDice1Hole: 60,
      optDice2Hole: 270,
      optDice3Hole: 380,
      optDice4Hole: 440,

      optWheel: true,
      optWheelCount: 2,
      optWheel1Hole: 120,
      optWheel2Hole: 340,
      optWheel3Hole: 220,
      optWheel4Hole: 410,

      optSkunkMarkers: true,
      optSkunkMarkerHole: 360,
      optDoubleSkunkMarkerHole: 240
    }
  },

  bottleneck: {
    key: 'bottleneck',
    name: 'Cutthroat Bottleneck',
    badge: '🛑 Choke Points',
    icon: '🛑',
    description: 'Choke-point heavy tactical battle. All 4 No Passing Lanes and 8 Stop Signs positioned at high-traffic zones to trap leaders behind opponents.',
    tags: ['Choke Points', 'High Tactical', 'Blocking'],
    hazardCount: 28,
    config: {
      optNoPassingLanes: true,
      optNoPassingLaneCount: 4,
      optNoPassingLane1Start: 45,
      optNoPassingLane2Start: 160,
      optNoPassingLane3Start: 270,
      optNoPassingLane4Start: 390,

      optJump: true,
      optJumpCount: 4,
      optJump1Start: 20, optJump1Dest: 40,
      optJump2Start: 120, optJump2Dest: 150,
      optJump3Start: 230, optJump3Dest: 260,
      optJump4Start: 350, optJump4Dest: 380,
      optJump5Start: 75, optJump5Dest: 110,
      optJump6Start: 145, optJump6Dest: 125,
      optJump7Start: 245, optJump7Dest: 280,
      optJump8Start: 365, optJump8Dest: 400,

      optJail: true,
      optJailCount: 2,
      optJail1Hole: 110,
      optJail2Hole: 330,

      optBar: true,
      optBarCount: 2,
      optBar1Hole: 190,
      optBar2Hole: 420,

      optTimeout: true,
      optTimeoutCount: 2,
      optTimeout1Hole: 80,
      optTimeout2Hole: 300,

      optDetour: true,
      optDetourCount: 1,
      optDetour1Hole: 15,
      optDetour2Hole: 235,

      optRoundAbout: true,
      optRoundAboutCount: 1,
      optRoundAbout1Hole: 215,
      optRoundAbout2Hole: 355,

      optSigns: true,
      optSignsCount: 4,
      optSigns1Hole: 100,
      optSigns2Hole: 215,
      optSigns3Hole: 350,
      optSigns4Hole: 455,

      optStopSign: true,
      optStopSignCount: 8,
      optStopSign1Hole: 40,
      optStopSign2Hole: 50,
      optStopSign3Hole: 155,
      optStopSign4Hole: 165,
      optStopSign5Hole: 265,
      optStopSign6Hole: 275,
      optStopSign7Hole: 385,
      optStopSign8Hole: 470,

      optDice: true,
      optDiceCount: 4,
      optDice1Hole: 50,
      optDice2Hole: 180,
      optDice3Hole: 290,
      optDice4Hole: 410,

      optWheel: true,
      optWheelCount: 4,
      optWheel1Hole: 65,
      optWheel2Hole: 190,
      optWheel3Hole: 300,
      optWheel4Hole: 420,

      optSkunkMarkers: true,
      optSkunkMarkerHole: 360,
      optDoubleSkunkMarkerHole: 240
    }
  }
});

export class ClashPresets {
  /**
   * Retrieves all available presets for a given track target (241, 361, or 481).
   * @param {number} [target=481]
   * @returns {Object}
   */
  static getPresets(target = 481) {
    const t = Number(target) || 481;
    if (t === 481) return CLASH_PRESETS_481;
    return ClashPresets.scalePresetsForTarget(CLASH_PRESETS_481, t);
  }

  /**
   * Scales a preset layout configuration proportionately for 241 or 361 targets.
   * @param {Object} presets
   * @param {number} target
   * @returns {Object}
   */
  static scalePresetsForTarget(presets, target) {
    const scale = target / 481;
    const scaled = {};

    for (const [key, preset] of Object.entries(presets)) {
      const cfg = { ...preset.config };
      for (const [prop, val] of Object.entries(cfg)) {
        if (typeof val === 'number' && (prop.endsWith('Hole') || prop.endsWith('Start') || prop.endsWith('Dest'))) {
          cfg[prop] = Math.max(5, Math.min(target - 5, Math.round(val * scale / 5) * 5));
        }
      }
      scaled[key] = {
        ...preset,
        config: cfg
      };
    }
    return scaled;
  }

  /**
   * Applies a preset directly into DOM fields and/or active settings object.
   * @param {string} presetKey
   * @param {Object} [context={}]
   * @returns {boolean}
   */
  static applyPreset(presetKey, context = {}) {
    const target = (context.settings && context.settings.target) || 481;
    const presets = ClashPresets.getPresets(target);
    const preset = presets[presetKey] || presets.standard;
    if (!preset) return false;

    const cfg = preset.config;
    const doc = context.document || (typeof document !== 'undefined' ? document : null);

    // Apply values to DOM form elements if available
    if (doc) {
      for (const [prop, val] of Object.entries(cfg)) {
        const el = doc.getElementById(prop);
        if (!el) continue;

        if (el.type === 'checkbox') {
          el.checked = Boolean(val);
          // Trigger toggle helper if available
          const toggleFn = (typeof window !== 'undefined') ? window[`toggle${prop.replace('opt', '')}Config`] : null;
          if (typeof toggleFn === 'function') {
            toggleFn(el.checked);
          }
        } else {
          el.value = String(val);
          el.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      // Sync placement buttons & conflict checkers
      if (typeof window !== 'undefined') {
        if (typeof window.enforceClashNoOverlap === 'function') {
          window.enforceClashNoOverlap();
        }
        if (typeof window.syncAllPlacementButtons === 'function') {
          window.syncAllPlacementButtons();
        }
        if (typeof window.readFormSettings === 'function' && typeof window.saveSettingsStore === 'function') {
          const updated = window.readFormSettings();
          updated.clashPreset = presetKey;
          window.saveSettingsStore(updated);
        }
      }
    }

    // Apply directly to settings object if passed
    if (context.settings) {
      Object.assign(context.settings, cfg);
      context.settings.clashPreset = presetKey;
    }

    // Trigger board re-render if active in game
    if (typeof window !== 'undefined' && typeof window.buildBoard === 'function') {
      window.buildBoard();
    }

    return true;
  }

  /**
   * Computes live track metrics from a set of placed obstacle items.
   * @param {Array<Object>} items
   * @param {number} [target=481]
   * @returns {Object}
   */
  static computeTrackMetrics(items = [], target = 481) {
    let penaltyTraps = 0;
    let forwardBoosts = 0;
    let backwardJumps = 0;
    let chokePoints = 0;
    let mysterySigns = 0;
    let stopSigns = 0;

    for (const item of items) {
      const type = item.type;
      if (['jail', 'bar', 'timeout'].includes(type)) penaltyTraps++;
      else if (type === 'noPassingLanes') chokePoints++;
      else if (type === 'stopSign') stopSigns++;
      else if (type === 'signs') mysterySigns++;
      else if (type === 'jump') {
        if (item.dest && item.hole) {
          if (item.dest > item.hole) forwardBoosts++;
          else backwardJumps++;
        } else {
          forwardBoosts++;
        }
      }
    }

    const totalHazards = items.length;
    const avgSpacing = totalHazards > 0 ? Math.round(target / totalHazards) : target;

    return {
      totalHazards,
      penaltyTraps,
      forwardBoosts,
      backwardJumps,
      chokePoints,
      stopSigns,
      mysterySigns,
      avgSpacing
    };
  }

  static getStorage(storage = null) {
    return storage || (typeof window !== 'undefined' && window.appStorage) || appStorage || (typeof localStorage !== 'undefined' ? localStorage : null);
  }

  /**
   * Retrieves all saved custom layouts from persistent storage.
   * @param {Object} [storage=null]
   * @returns {Array<Object>}
   */
  static getCustomLayouts(storage = null) {
    const s = ClashPresets.getStorage(storage);
    if (!s) return [];
    try {
      const raw = s.getItem(CLASH_CUSTOM_LAYOUTS_KEY);
      if (!raw) return [];
      const data = JSON.parse(raw);
      return Array.isArray(data) ? data : [];
    } catch (e) {
      console.warn('[ClashPresets] Failed to parse custom layouts:', e);
      return [];
    }
  }

  /**
   * Saves a custom layout (either creates new or updates existing).
   * @param {Object} layout
   * @param {Object} [storage=null]
   * @returns {Object} The saved layout object
   */
  static saveCustomLayout(layout, storage = null, isImport = false) {
    if (!layout || typeof layout !== 'object') throw new Error('Invalid layout object');
    const s = ClashPresets.getStorage(storage);
    const layouts = ClashPresets.getCustomLayouts(s);
    const now = Date.now();

    const sanitized = LayoutSanitizer.sanitizeLayout(layout, 'clash', {
      regenerateId: isImport || !layout.id
    });
    sanitized.metrics = ClashPresets.computeTrackMetrics(sanitized.items, sanitized.target);

    let targetLayout = null;
    if (!isImport && layout.id) {
      const existingIdx = layouts.findIndex(l => l.id === sanitized.id);
      if (existingIdx !== -1) {
        targetLayout = {
          ...layouts[existingIdx],
          ...sanitized,
          updatedAt: now
        };
        layouts[existingIdx] = targetLayout;
      }
    }

    if (!targetLayout) {
      targetLayout = sanitized;
      layouts.unshift(targetLayout);
    }

    const capped = LayoutSanitizer.enforceStorageQuota(layouts, MAX_CUSTOM_LAYOUTS);

    if (s) {
      try {
        s.setItem(CLASH_CUSTOM_LAYOUTS_KEY, JSON.stringify(capped));
      } catch (e) {
        console.error('[ClashPresets] Failed to save custom layouts:', e);
        throw new Error('Failed to save layout to storage: Quota exceeded or storage unavailable.');
      }
    }
    return targetLayout;
  }

  /**
   * Renames a custom layout by ID.
   * @param {string} layoutId
   * @param {string} newName
   * @param {Object} [storage=null]
   * @returns {boolean}
   */
  static renameCustomLayout(layoutId, newName, storage = null) {
    if (!layoutId || !newName || !newName.trim()) return false;
    const s = ClashPresets.getStorage(storage);
    const layouts = ClashPresets.getCustomLayouts(s);
    const idx = layouts.findIndex(l => l.id === layoutId);
    if (idx === -1) return false;
    layouts[idx].name = newName.trim();
    layouts[idx].updatedAt = Date.now();
    if (s) {
      try {
        s.setItem(CLASH_CUSTOM_LAYOUTS_KEY, JSON.stringify(layouts));
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  /**
   * Deletes a custom layout by ID.
   * @param {string} layoutId
   * @param {Object} [storage=null]
   * @returns {boolean}
   */
  static deleteCustomLayout(layoutId, storage = null) {
    if (!layoutId) return false;
    const s = ClashPresets.getStorage(storage);
    const layouts = ClashPresets.getCustomLayouts(s);
    const filtered = layouts.filter(l => l.id !== layoutId);
    if (filtered.length === layouts.length) return false;
    if (s) {
      try {
        s.setItem(CLASH_CUSTOM_LAYOUTS_KEY, JSON.stringify(filtered));
        return true;
      } catch (e) {
        return false;
      }
    }
    return false;
  }

  /**
   * Clones / duplicates an existing custom layout.
   * @param {string} layoutId
   * @param {Object} [storage=null]
   * @returns {Object|null}
   */
  static duplicateCustomLayout(layoutId, storage = null) {
    const s = ClashPresets.getStorage(storage);
    const layouts = ClashPresets.getCustomLayouts(s);
    const source = layouts.find(l => l.id === layoutId);
    if (!source) return null;
    const clone = {
      ...source,
      id: null,
      name: `${source.name} (Copy)`
    };
    return ClashPresets.saveCustomLayout(clone, s);
  }

  /**
   * Serializes a layout or all layouts into a shareable JSON string.
   * @param {string} [layoutId=null] If null, exports all custom layouts
   * @param {Object} [storage=null]
   * @returns {string}
   */
  static exportLayout(layoutId = null, storage = null) {
    const s = ClashPresets.getStorage(storage);
    const layouts = ClashPresets.getCustomLayouts(s);
    if (layoutId) {
      const single = layouts.find(l => l.id === layoutId);
      const prepared = LayoutSanitizer.prepareForExport(single || null);
      return JSON.stringify(prepared, null, 2);
    }
    const prepared = LayoutSanitizer.prepareForExport(layouts);
    return JSON.stringify(prepared, null, 2);
  }

  /**
   * Imports a layout from a JSON string.
   * @param {string} jsonStr
   * @param {Object} [storage=null]
   * @returns {Object}
   */
  static importLayout(jsonStr, storage = null) {
    const parsed = LayoutSanitizer.parseJsonSafely(jsonStr);
    const s = ClashPresets.getStorage(storage);
    if (Array.isArray(parsed)) {
      if (parsed.length > 20) {
        throw new Error('Cannot import more than 20 layouts at once.');
      }
      let lastSaved = null;
      for (const item of parsed) {
        lastSaved = ClashPresets.saveCustomLayout(item, s, true);
      }
      return lastSaved;
    }
    return ClashPresets.saveCustomLayout(parsed, s, true);
  }
}

if (typeof window !== 'undefined') {
  window.ClashPresets = ClashPresets;
  window.CLASH_PRESETS_481 = CLASH_PRESETS_481;
  window.CLASH_CUSTOM_LAYOUTS_KEY = CLASH_CUSTOM_LAYOUTS_KEY;
  window.applyClashPreset = (key, ctx) => ClashPresets.applyPreset(key, ctx);
}
